import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";

// LQC-1301 — Trade-wide settings (Auto Affirm, Cover Spread) can be adjusted
// from the blotter past the `Sent` status, but each setting has its own status
// ceiling. This module is the single, pure source of truth for "is this
// trade-wide setting still editable at this status?" — reused by the cell
// renderer, each dropdown's disabled state, and the tests.
//
// Ceilings (confirmed with product as the v1 default — see plan):
//   - Auto Affirm: editable only BEFORE its auto-affirm trigger fires.
//       RFQ   -> trigger is Executed,             so editable up to Awarded.
//       Order -> trigger is Filled / No More Fills, so editable up to Working.
//   - Cover Spread: editable all the way through to Affirmed (including the
//       Executed -> Affirmed change-request tail). Does not apply to Orders.
//   - Terminal / dead statuses: nothing is editable.

export type TradeWideSettingKey = "autoAffirm" | "releaseCoverSpread";

const ES = ExecutionStrategyStatus;

// Trade is finished / withdrawn — no settings can change.
const TERMINAL_STATUSES: ExecutionStrategyStatus[] = [
  ES.Cancelled,
  ES.Rejected,
  ES.NotDealt,
  ES.DealtAway,
  ES.Recalled,
  ES.ReCreated,
];

// Auto Affirm — RFQ: editable up to and including Awarded (before Executed).
const AUTO_AFFIRM_EDITABLE_RFQ: ExecutionStrategyStatus[] = [
  ES.New,
  ES.Sent,
  ES.ReSent,
  ES.Acknowledged,
  ES.Quoted,
  ES.OffQuote,
  ES.ReQuoteRequested,
  ES.QuoteExpired,
  ES.Awarded,
];

// Auto Affirm — Order: editable up to and including Working (before Filled).
const AUTO_AFFIRM_EDITABLE_ORDER: ExecutionStrategyStatus[] = [
  ES.New,
  ES.Sent,
  ES.ReSent,
  ES.Received,
  ES.Acknowledged,
  ES.Working,
];

// Cover Spread — editable through to Affirmed (RFQ only). Builds on the
// Auto Affirm RFQ window and extends it across the Executed -> Affirmed tail.
const COVER_SPREAD_EDITABLE: ExecutionStrategyStatus[] = [
  ...AUTO_AFFIRM_EDITABLE_RFQ,
  ES.Executed,
  ES.ExecutedRPChangeRequested,
  ES.ExecutedRPAgreeChange,
  ES.ExecutedOPChangeRequested,
  ES.Affirmed,
];

// LQC-2271 - Side (quoteType) / Direction (releaseDirection) are only adjustable
// while the trade is still quoting. At Awarded the side has already been
// released, so the pair is removed from the Trade Settings sidebar entirely.
const SIDE_DIRECTION_EDITABLE: ExecutionStrategyStatus[] = [
  ES.New,
  ES.Sent,
  ES.ReSent,
  ES.Received,
  ES.Acknowledged,
  ES.Quoted,
  ES.OffQuote,
  ES.ReQuoteRequested,
  ES.QuoteExpired,
  ES.Working,
];

function matches(
  status: ExecutionStrategyStatus | string | undefined,
  allowed: ExecutionStrategyStatus[],
): boolean {
  return allowed.includes(status as ExecutionStrategyStatus);
}

/**
 * Whether a trade has been *sent* to a counterparty and is therefore eligible
 * for trade-settings adjustment in the side panel. New (pre-send) trades and
 * terminal/dead trades are NOT — the panel shows an informational notice for
 * those instead of the editable settings.
 */
export function isSentTradeStatus(
  status: ExecutionStrategyStatus | string | undefined,
): boolean {
  if (status === ES.New) return false;
  if (matches(status, TERMINAL_STATUSES)) return false;
  return true;
}

/**
 * Whether a specific trade-wide setting is still editable at the given status.
 * `isOrder` distinguishes the Order vs RFQ ceilings (Auto Affirm differs; Cover
 * Spread does not apply to Orders at all).
 */
export function isTradeWideSettingEditable(
  key: TradeWideSettingKey,
  status: ExecutionStrategyStatus | string | undefined,
  isOrder: boolean,
): boolean {
  if (matches(status, TERMINAL_STATUSES)) {
    return false;
  }
  if (key === "releaseCoverSpread") {
    if (isOrder) return false; // Cover Spread is not an Order setting.
    return matches(status, COVER_SPREAD_EDITABLE);
  }
  // autoAffirm
  return matches(
    status,
    isOrder ? AUTO_AFFIRM_EDITABLE_ORDER : AUTO_AFFIRM_EDITABLE_RFQ,
  );
}

/**
 * LQC-2271 - whether Side / Direction can still be changed at this status.
 * Pre-Awarded only; every later (and terminal) status falls outside the list.
 */
export function isSideAndDirectionEditable(
  status: ExecutionStrategyStatus | string | undefined,
): boolean {
  return matches(status, SIDE_DIRECTION_EDITABLE);
}

/**
 * Whether ANY trade-wide setting is still editable at this status. Drives
 * whether the blotter settings icon is shown active vs muted (preview-only).
 */
export function isAnySettingEditable(
  status: ExecutionStrategyStatus | string | undefined,
  isOrder: boolean,
): boolean {
  return (
    isTradeWideSettingEditable("autoAffirm", status, isOrder) ||
    isTradeWideSettingEditable("releaseCoverSpread", status, isOrder)
  );
}

/**
 * The RP has requested a change on an executed trade — drives the pulsing
 * icon affordance on the blotter (LQC-637 link in LQC-1301).
 */
export function isRpChangeRequested(
  status: ExecutionStrategyStatus | string | undefined,
): boolean {
  return status === ExecutionStrategyStatus.ExecutedRPChangeRequested;
}
